'use client';

import React, { useState, useEffect, useRef } from 'react';

export interface UseLazyLoadingOptions {
  /** Initial number of items to show */
  initialCount?: number;
  /** Number of items to load each time */
  loadMoreCount?: number;
  /** Intersection observer threshold (0-1) */
  threshold?: number;
  /** Root margin for intersection observer */
  rootMargin?: string;
  /** Loading delay in milliseconds */
  loadingDelay?: number;
}

export interface UseLazyLoadingReturn<T> {
  /** Currently visible items */
  visibleItems: T[];
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Whether currently loading more items */
  isLoading: boolean;
  /** Ref to attach to the sentinel element */
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  /** Manually trigger loading more items */
  loadMore: () => void;
}

/**
 * Generic lazy loading hook for any array of items
 * Uses Intersection Observer to detect when user scrolls near the bottom
 *
 * @param items - Array of items to lazy load
 * @param enabled - Whether lazy loading is enabled
 * @param options - Configuration options
 * @returns Object with visible items, loading state, and sentinel ref
 */
export function useLazyLoading<T>(
  items: T[],
  enabled: boolean = true,
  options: UseLazyLoadingOptions = {}
): UseLazyLoadingReturn<T> {
  const {
    initialCount = 5,
    loadMoreCount = 5,
    threshold = 0.1,
    rootMargin = '100px',
    loadingDelay = 300,
  } = options;

  const [visibleCount, setVisibleCount] = useState<number>(
    enabled ? Math.min(initialCount, items.length) : items.length
  );
  const [isLoading, setIsLoading] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Reset visible count when items change or lazy loading is toggled
  useEffect(() => {
    setVisibleCount(enabled ? Math.min(initialCount, items.length) : items.length);
  }, [items.length, enabled, initialCount]);

  // Set up intersection observer
  useEffect(() => {
    if (!enabled || visibleCount >= items.length) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoading) {
          setIsLoading(true);

          // Simulate loading delay for better UX
          setTimeout(() => {
            setVisibleCount((prev) => Math.min(prev + loadMoreCount, items.length));
            setIsLoading(false);
          }, loadingDelay);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observerRef.current = observer;

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    enabled,
    visibleCount,
    items.length,
    threshold,
    rootMargin,
    loadMoreCount,
    loadingDelay,
    isLoading,
  ]);

  // Manual load more function
  const loadMore = () => {
    if (!isLoading && visibleCount < items.length) {
      setIsLoading(true);
      setTimeout(() => {
        setVisibleCount((prev) => Math.min(prev + loadMoreCount, items.length));
        setIsLoading(false);
      }, loadingDelay);
    }
  };

  return {
    visibleItems: items.slice(0, visibleCount),
    hasMore: visibleCount < items.length,
    isLoading,
    sentinelRef,
    loadMore,
  };
}
